import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import FormCheckbox from '../../../components/general/FormCheckbox';
import FormSelect from '../../../components/general/FormSelect';
import FormMultiSelect from '../../../components/general/FormMultiSelect';
import FormTextField from '../../../components/general/FormTextField';
import { Button, Collapse, message } from 'antd';


const { Panel } = Collapse;



const transformEditProduct = (editData, tables) => {
  if (!editData || !editData.ProductDetail) return [];

  return editData.ProductDetail.map((detail) => {
    // Find branches for this detail
    const detailBranches = editData.ProductDetailBranch
      ?.filter(b =>
        b.ProductDetailId === detail.ProductDetailId &&
        b.SizeId === detail.SizeId &&
        b.FlavourId === detail.FlavourId
      )
      .map(b => ({
        BranchId: b.BranchId,
        BranchName: tables.Table6?.find(branch => branch.BranchId === b.BranchId)?.BranchName || '',
        RemoteId: b.RemoteId || ""
      })) || [];

    // Find order sources for this detail (including BranchId)
    const detailOrderSourcesWithBranch = editData.ProductDetailOrderSource
      ?.filter(os =>
        os.ProductDetailId === detail.ProductDetailId &&
        os.SizeId === detail.SizeId &&
        os.FlavourId === detail.FlavourId
      ) || [];

    // Check if any order source has a non-null BranchId
    const hasBranchWiseData = detailOrderSourcesWithBranch.some(os => os.BranchId != null);

    // Separate order sources with and without BranchId
    const branchWiseOrderSources = detailOrderSourcesWithBranch.filter(os => os.BranchId != null);
    const regularOrderSources = detailOrderSourcesWithBranch.filter(os => os.BranchId == null);

    // Merge with all available branches (add missing ones)
    const allBranches = tables.Table6?.map(branch => {
      const existingBranch = detailBranches.find(b => b.BranchId === branch.BranchId);
      return existingBranch || {
        BranchId: branch.BranchId,
        BranchName: branch.BranchName,
        RemoteId: ""
      };
    }) || [];

    // Merge with all available order sources (add missing ones) - for non-branch-wise
    const allOrderSources = tables.Table4?.map(source => {
      const existingSource = regularOrderSources.find(os => os.OrderSourceId === source.OrderSourceId);
      return existingSource ? {
        OrderSourceId: source.OrderSourceId,
        OrderSourceName: tables.Table4?.find(s => s.OrderSourceId === source.OrderSourceId)?.OrderSource || '',
        Price: existingSource.Price?.toString() || ""
      } : {
        OrderSourceId: source.OrderSourceId,
        OrderSourceName: source.OrderSource,
        Price: ""
      };
    }) || [];

    // Build branchWiseGroups if there are BranchIds
    let branchWiseGroups = [];
    if (hasBranchWiseData && branchWiseOrderSources.length > 0) {
      // Group order sources by BranchId
      const branchOrderSourceMap = new Map();

      branchWiseOrderSources.forEach(os => {
        if (os.BranchId != null && os.Price != null && os.Price !== "") {
          const branchId = os.BranchId;
          if (!branchOrderSourceMap.has(branchId)) {
            branchOrderSourceMap.set(branchId, []);
          }
          branchOrderSourceMap.get(branchId).push({
            OrderSourceId: os.OrderSourceId,
            OrderSourceName: tables.Table4?.find(s => s.OrderSourceId === os.OrderSourceId)?.OrderSource || '',
            Price: os.Price?.toString() || ""
          });
        }
      });

      // Group branches that share the same order source prices
      const groupMap = new Map();
      branchOrderSourceMap.forEach((orderSources, branchId) => {
        // Create a key based on order source prices
        const priceKey = orderSources
          .map(os => `${os.OrderSourceId}:${os.Price}`)
          .sort()
          .join('|');

        if (!groupMap.has(priceKey)) {
          groupMap.set(priceKey, {
            id: Date.now() + Math.random() + Math.random() * branchId,
            selectedBranches: [],
            orderSources: tables.Table4?.map(source => {
              const existing = orderSources.find(os => os.OrderSourceId === source.OrderSourceId);
              return existing || {
                OrderSourceId: source.OrderSourceId,
                OrderSourceName: source.OrderSource,
                Price: ""
              };
            }) || []
          });
        }
        groupMap.get(priceKey).selectedBranches.push(branchId);
      });

      branchWiseGroups = Array.from(groupMap.values());
    }

    return {
      id: Date.now() + Math.random(),
      ProductDetailId: detail.ProductDetailId,
      SizeId: detail.SizeId?.toString() || "",
      FlavourId: detail.FlavourId?.toString() || "",
      Price: detail.Price?.toString() || "",
      PriceBeforeDiscount: detail.PriceBeforeDiscount?.toString() || "",
      RemoteId: detail.RemoteId || "",
      IsBestSeller: detail.IsBestSeller,
      IsPromotion: detail.IsPromotion,
      IsEnable: detail.IsEnable,
      OnlyForDeal: detail.OnlyForDeal,
      BranchWise: hasBranchWiseData,
      branches: allBranches,
      orderSources: allOrderSources,
      branchWiseGroups: branchWiseGroups
    };
  });
};





const ProductDetailsManager = ({ supportingTable, onDetailsChange, editProduct = null, isProductUpdate, resetTrigger }) => {



  const initialDetails = useMemo(() => {
    if (isProductUpdate && editProduct) {
      return transformEditProduct(editProduct, supportingTable);
    }
    return [];
  }, [isProductUpdate, editProduct]);

  const [productDetails, setProductDetails] = useState(initialDetails);

  useEffect(() => {
    setProductDetails([]);
  }, [resetTrigger]);


  useEffect(() => {

    if (isProductUpdate && initialDetails.length > 0) {
      setProductDetails(initialDetails);
    }
  }, [isProductUpdate, initialDetails]);



  useEffect(() => {
    onDetailsChange?.(formattedData);
  }, [productDetails]);



  const createEmptyDetail = () => ({
    id: Date.now() + Math.random(),
    ProductDetailId: null,
    SizeId: "",
    FlavourId: "",
    Price: "",
    PriceBeforeDiscount: "",
    RemoteId: "",
    IsBestSeller: false,
    IsPromotion: false,
    IsEnable: true,
    OnlyForDeal: false,
    BranchWise: false,
    branches: supportingTable.Table6?.map(branch => ({
      BranchId: branch.BranchId,
      BranchName: branch.BranchName,
      RemoteId: ""
    })) || [],
    orderSources: supportingTable.Table4?.map(source => ({
      OrderSourceId: source.OrderSourceId,
      OrderSourceName: source.OrderSource,
      Price: ""
    })) || [],
    branchWiseGroups: []
  });



  const formattedData = useMemo(() => {
    const ProductDetail = productDetails.map(detail => ({
      ProductDetailId: detail.ProductDetailId,
      SizeId: detail.SizeId ? Number(detail.SizeId) : null,
      FlavourId: detail.FlavourId ? Number(detail.FlavourId) : null,
      Price: detail.Price ? Number(detail.Price) : 0,
      PriceBeforeDiscount: detail.PriceBeforeDiscount ? Number(detail.PriceBeforeDiscount) : 0,
      FuturePrice: 0,
      RemoteId: detail.RemoteId || "",
      OnlyForDeal: detail.OnlyForDeal,
      IsBestSeller: detail.IsBestSeller,
      IsPromotion: detail.IsPromotion,
      IsEnable: detail.IsEnable, // ✅ properly included now
    }));

    const ProductDetailBranch = productDetails.flatMap(detail =>
      detail.branches
        .filter(b => b.RemoteId)
        .map(b => ({
          ProductDetailId: detail.ProductDetailId,
          SizeId: detail.SizeId ? Number(detail.SizeId) : null,
          FlavourId: detail.FlavourId ? Number(detail.FlavourId) : null,
          RemoteId: b.RemoteId,
          BranchId: b.BranchId,
        }))
    );

    const ProductDetailOrderSource = productDetails.flatMap(detail => {
      if (detail.BranchWise) {
        // If BranchWise is checked, include order sources from branchWiseGroups with BranchIds
        return (detail.branchWiseGroups || []).flatMap(group =>
          (group.selectedBranches || []).flatMap(branchId =>
            (group.orderSources || [])
              .filter(os => os.Price && os.Price.toString().trim() !== "")
              .map(os => ({
                ProductDetailId: detail.ProductDetailId,
                SizeId: detail.SizeId ? Number(detail.SizeId) : null,
                FlavourId: detail.FlavourId ? Number(detail.FlavourId) : null,
                Price: Number(os.Price),
                OrderSourceId: os.OrderSourceId,
                BranchId: branchId
              }))
          )
        );
      } else {
        // If BranchWise is unchecked, include order sources with BranchId as null
        return detail.orderSources
          .filter(os => os.Price && os.Price.toString().trim() !== "")
          .map(os => ({
            ProductDetailId: detail.ProductDetailId,
            SizeId: detail.SizeId ? Number(detail.SizeId) : null,
            FlavourId: detail.FlavourId ? Number(detail.FlavourId) : null,
            Price: Number(os.Price),
            OrderSourceId: os.OrderSourceId,
            BranchId: null
          }));
      }
    });

    return { ProductDetail, ProductDetailBranch, ProductDetailOrderSource };
  }, [productDetails]);

















  const handleAddDetail = () => {
    const newDetails = [...productDetails, createEmptyDetail()];
    setProductDetails(newDetails);
    onDetailsChange?.(formattedData);
  };

  const handleRemoveDetail = (id) => {
    setProductDetails(prev => prev.filter(detail => detail.ProductDetailId !== id));
  };



  const handleDetailChange = (id, fieldData) => {
    const field = fieldData?.name;
    const value =
      typeof fieldData?.checked === "boolean"
        ? fieldData.checked
        : fieldData?.value !== undefined
          ? fieldData.value
          : fieldData;

    const newDetails = productDetails.map(detail =>
      detail.id === id ? { ...detail, [field]: value } : detail
    );

    setProductDetails(newDetails);
  };


  const handleBranchRemoteIdChange = (detailId, branchId, fieldData) => {
    const remoteId = fieldData?.value !== undefined ? fieldData.value : fieldData;

    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          branches: detail.branches.map(branch =>
            branch.BranchId === branchId ? { ...branch, RemoteId: remoteId } : branch
          )
        }
        : detail
    );
    setProductDetails(newDetails);
    setTimeout(() => onDetailsChange?.(formattedData), 0);
  };

  const handleOrderSourcePriceChange = (detailId, sourceId, fieldData) => {
    const price = fieldData?.value !== undefined ? fieldData.value : fieldData;

    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          orderSources: detail.orderSources.map(source =>
            source.OrderSourceId === sourceId ? { ...source, Price: price } : source
          )
        }
        : detail
    );
    setProductDetails(newDetails);
    setTimeout(() => onDetailsChange?.(formattedData), 0);
  };

  const createEmptyBranchWiseGroup = () => ({
    id: Date.now() + Math.random(),
    selectedBranches: [],
    orderSources: supportingTable.Table4?.map(source => ({
      OrderSourceId: source.OrderSourceId,
      OrderSourceName: source.OrderSource,
      Price: ""
    })) || []
  });

  const validateBranchWiseGroups = (detail) => {
    const groups = detail.branchWiseGroups || [];

    // Check if there are any groups
    if (groups.length === 0) {
      return { isValid: true };
    }

    // Validate each group
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];

      // Check if at least 1 branch is selected
      if (!group.selectedBranches || group.selectedBranches.length === 0) {
        return {
          isValid: false,
          message: `Group ${i + 1} must have at least 1 branch selected`
        };
      }

      // Check if at least 1 order source has a price
      const hasOrderSourcePrice = group.orderSources?.some(
        source => source.Price && source.Price.toString().trim() !== ""
      );

      if (!hasOrderSourcePrice) {
        return {
          isValid: false,
          message: `Group ${i + 1} must have at least 1 order source with a price`
        };
      }
    }

    return { isValid: true };
  };

  const handleAddBranchWiseGroup = (detailId) => {
    const detail = productDetails.find(d => d.id === detailId);
    if (!detail) return;

    // Validate existing groups before adding a new one
    const validation = validateBranchWiseGroups(detail);

    if (!validation.isValid) {
      message.warning(validation.message);
      return;
    }

    const newGroup = createEmptyBranchWiseGroup();
    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          branchWiseGroups: [newGroup, ...(detail.branchWiseGroups || [])]
        }
        : detail
    );
    setProductDetails(newDetails);
  };

  const handleBranchWiseGroupBranchChange = (detailId, groupId, branchIds) => {
    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          branchWiseGroups: detail.branchWiseGroups?.map(group => {
            if (group.id === groupId) {
              // Update the current group with new branch selections
              return { ...group, selectedBranches: branchIds || [] };
            } else {
              // Remove any branches from other groups that are now selected in the current group
              const newSelectedBranches = (group.selectedBranches || []).filter(
                branchId => !(branchIds || []).includes(branchId)
              );
              return { ...group, selectedBranches: newSelectedBranches };
            }
          }) || []
        }
        : detail
    );
    setProductDetails(newDetails);
    setTimeout(() => onDetailsChange?.(formattedData), 0);
  };

  const getAvailableBranchesForGroup = (detail, currentGroupId) => {
    // Get all branches selected in other groups
    const branchesInOtherGroups = detail.branchWiseGroups
      ?.filter(group => group.id !== currentGroupId)
      .flatMap(group => group.selectedBranches || []) || [];

    // Filter out branches that are already in other groups
    return supportingTable.Table6?.filter(
      branch => !branchesInOtherGroups.includes(branch.BranchId)
    ) || [];
  };

  const handleBranchWiseGroupOrderSourcePriceChange = (detailId, groupId, sourceId, fieldData) => {
    const price = fieldData?.value !== undefined ? fieldData.value : fieldData;

    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          branchWiseGroups: detail.branchWiseGroups?.map(group =>
            group.id === groupId
              ? {
                ...group,
                orderSources: group.orderSources.map(source =>
                  source.OrderSourceId === sourceId ? { ...source, Price: price } : source
                )
              }
              : group
          ) || []
        }
        : detail
    );
    setProductDetails(newDetails);
    setTimeout(() => onDetailsChange?.(formattedData), 0);
  };

  const handleRemoveBranchWiseGroup = (detailId, groupId) => {
    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          branchWiseGroups: detail.branchWiseGroups?.filter(group => group.id !== groupId) || []
        }
        : detail
    );
    setProductDetails(newDetails);
  };














  return (
    <div className="pt-2 w-full">
      <div className="max-w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Product Details</h2>
          <Button
            onClick={handleAddDetail}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Create Product Detail
          </Button>
        </div>

        {productDetails.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
            No product details added yet. Click "Create Product Detail" to get started.
          </div>
        ) : (
          <Collapse
            accordion
            className="bg-gray-50 rounded-lg"
            defaultActiveKey={[productDetails[productDetails.length - 1]?.id]}
          >
            {productDetails.map((detail, index) => (
              <Panel
                key={detail.id}
                header={
                  <div className="flex justify-between w-full items-center">
                    <span className="text-gray-700 font-medium">
                      Product Detail
                      {/* {supportingTable.Table2?.find(s => s.SizeId == detail.SizeId)?.SizeName || 'N/A'} |  */}
                      {/* {supportingTable.Table3?.find(f => f.FlavourId == detail.FlavourId)?.FlavourName || 'N/A'} */}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveDetail(detail.ProductDetailId);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors bg-red-100"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-1 mb-6">
                  <FormSelect
                    label="Size"
                    name="SizeId"
                    value={detail.SizeId}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                    listItem={supportingTable.Table2}
                    idName="SizeId"
                    valueName="SizeName"
                    required={true}
                  />

                  <FormSelect
                    label="Variant"
                    name="FlavourId"
                    value={detail.FlavourId}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                    listItem={supportingTable.Table3}
                    idName="FlavourId"
                    valueName="FlavourName"
                    required={true}
                  />

                  <FormTextField
                    label="Price"
                    name="Price"
                    type="number"
                    value={detail.Price}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                    required={true}
                  />

                  <FormTextField
                    label="Price Before Discount"
                    name="PriceBeforeDiscount"
                    type="number"
                    value={detail.PriceBeforeDiscount}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormTextField
                    label="Remote Code"
                    name="RemoteId"
                    value={detail.RemoteId}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-1 mb-6">
                  <FormCheckbox
                    label="Cart Suggestion"
                    name="IsPromotion"
                    checked={detail.IsPromotion}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormCheckbox
                    label="Popular Product"
                    name="IsBestSeller"
                    checked={detail.IsBestSeller}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormCheckbox
                    label="Enable"
                    name="IsEnable"
                    checked={detail.IsEnable}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormCheckbox
                    label="Only For Deal"
                    name="OnlyForDeal"
                    checked={detail.OnlyForDeal}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className='border p-4 rounded-xl'>
                    <div className='flex justify-between items-center'>
                      <h4 className="text-md font-semibold text-gray-700 mb-3">Branches</h4>
                      <h4 className="text-md font-semibold text-gray-700 mb-3">Branches Remote Code</h4>
                    </div>
                    <div className="rounded-lg">
                      <div className="grid grid-cols-6 gap-3">
                        {detail.branches.map((branch) => (
                          <React.Fragment key={branch.BranchId}>
                            <span className="text-sm font-medium text-gray-600 flex items-center">{branch.BranchName}</span>
                            <FormTextField
                              label=""
                              name={`branch_${branch.BranchId}`}
                              value={branch.RemoteId}
                              onChange={(data) => handleBranchRemoteIdChange(detail.id, branch.BranchId, data)}
                            />
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className='border p-4 rounded-xl'>
                    <div className='flex justify-between items-center'>
                      <div className="flex items-center gap-3">
                        <h4 className="text-md font-semibold text-gray-700 mb-3">
                          <FormCheckbox
                            label="Branch Wise - Order Sources"
                            name="BranchWise"
                            checked={detail.BranchWise}
                            onChange={(data) => handleDetailChange(detail.id, data)}
                          />
                        </h4>
                      </div>
                      {!detail.BranchWise && (
                        <h4 className="text-md font-semibold text-gray-700 mb-3">Prices</h4>
                      )}
                    </div>
                    <div className="rounded-lg">
                      {detail.BranchWise ? (
                        <div className="space-y-4">
                          <Button
                            onClick={() => handleAddBranchWiseGroup(detail.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Plus size={20} />
                            Add
                          </Button>
                          {detail.branchWiseGroups?.map((group, groupIndex) => (
                            <div key={group.id} className="border p-4 rounded-lg bg-gray-50">
                              <div className="flex justify-between items-center mb-4">
                                <h5 className="text-sm font-semibold text-gray-700">Group {groupIndex + 1}</h5>
                                <button
                                  onClick={() => handleRemoveBranchWiseGroup(detail.id, group.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors bg-red-100"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              <div className="mb-4">
                                <FormMultiSelect
                                  label="Branches"
                                  name={`branches_${group.id}`}
                                  value={group.selectedBranches || []}
                                  onChange={(selectedValues) => {
                                    handleBranchWiseGroupBranchChange(detail.id, group.id, selectedValues || []);
                                  }}
                                  listItem={getAvailableBranchesForGroup(detail, group.id)}
                                  idName="BranchId"
                                  valueName="BranchName"
                                />
                              </div>
                              <div className="grid grid-cols-8 gap-3">
                                {group.orderSources.map((source) => (
                                  <React.Fragment key={source.OrderSourceId}>
                                    <span className="text-sm font-medium text-gray-600 flex items-center">{source.OrderSourceName}</span>
                                    <FormTextField
                                      label=""
                                      name={`group_${group.id}_source_${source.OrderSourceId}`}
                                      type="number"
                                      value={source.Price}
                                      onChange={(data) => handleBranchWiseGroupOrderSourcePriceChange(detail.id, group.id, source.OrderSourceId, data)}
                                    />
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-8 gap-3">
                          {detail.orderSources.map((source) => (
                            <React.Fragment key={source.OrderSourceId}>
                              <span className="text-sm font-medium text-gray-600 flex items-center">{source.OrderSourceName}</span>
                              <FormTextField
                                label=""
                                name={`source_${source.OrderSourceId}`}
                                type="number"
                                value={source.Price}
                                onChange={(data) => handleOrderSourcePriceChange(detail.id, source.OrderSourceId, data)}
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Panel>
            ))}
          </Collapse>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsManager;